import { createParticipantLayoutScreenShareId } from "./participant-layout-participants";

function getMediaStreamConstructor() {
  if (typeof window !== "undefined" && window.MediaStream) {
    return window.MediaStream;
  }

  if (typeof global !== "undefined" && global.MediaStream) {
    return global.MediaStream;
  }

  return null;
}

const participantLayoutMediaStreamCache = new WeakMap();

export function createParticipantLayoutMediaStream(track) {
  if (!track) {
    return null;
  }

  const cachedMediaStream = participantLayoutMediaStreamCache.get(track);

  if (cachedMediaStream) {
    return cachedMediaStream;
  }

  const MediaStreamConstructor = getMediaStreamConstructor();
  let mediaStream;

  if (MediaStreamConstructor) {
    mediaStream = new MediaStreamConstructor([track]);
  } else {
    mediaStream = {
      getTracks() {
        return [track];
      },
      getAudioTracks() {
        return track.kind === "audio" ? [track] : [];
      },
      getVideoTracks() {
        return track.kind === "video" ? [track] : [];
      }
    };
  }

  participantLayoutMediaStreamCache.set(track, mediaStream);

  return mediaStream;
}

function getDialogRemoteShareConsumers(dialog, mySessionId) {
  return Array.from(dialog?._consumers?.entries?.() || [])
    .map(([consumerKey, consumer]) => ({ consumer, consumerKey }))
    .filter(({ consumer }) => {
      return (
        !consumer?.closed &&
        consumer?.track?.kind === "video" &&
        Boolean(consumer?.appData?.share) &&
        Boolean(consumer?.appData?.peerId) &&
        consumer.appData.peerId !== mySessionId
      );
    });
}

function getDialogRemoteCameraConsumers(dialog) {
  const consumers = Array.from(dialog?._consumers?.values?.() || []);

  return consumers.filter(consumer => {
    return (
      !consumer?.closed &&
      consumer?.track?.kind === "video" &&
      consumer?.appData?.share !== true &&
      Boolean(consumer?.appData?.peerId)
    );
  });
}

function getDialogLocalCameraTrack(dialog) {
  const cameraProducer = dialog?._cameraProducer;

  if (!cameraProducer || cameraProducer.closed) {
    return null;
  }

  return cameraProducer.track || null;
}

function getDialogLocalShareTrack(dialog) {
  const shareProducer = dialog?._shareProducer;

  if (!shareProducer || shareProducer.closed) {
    return null;
  }

  return shareProducer.track || null;
}

function getTrackKey(track) {
  if (!track) {
    return "";
  }

  return track.id || track.label || `${track.kind || "track"}`;
}

function getDialogLocalShare(dialog, mySessionId) {
  const localShareTrack = getDialogLocalShareTrack(dialog);

  if (!localShareTrack || !mySessionId) {
    return null;
  }

  const shareId = dialog?._shareProducer?.id || getTrackKey(localShareTrack);

  return {
    id: createParticipantLayoutScreenShareId({ ownerId: mySessionId, shareId }),
    isLocal: true,
    mediaStream: createParticipantLayoutMediaStream(localShareTrack),
    ownerId: mySessionId,
    trackKey: getTrackKey(localShareTrack)
  };
}

function getDialogRemoteScreenShares(dialog, mySessionId) {
  return getDialogRemoteShareConsumers(dialog, mySessionId).map(({ consumer, consumerKey }) => {
    const ownerId = consumer.appData.peerId;
    const shareId = consumer.appData.producerId || consumer.appData.sourceId || consumer.id || consumerKey;

    return {
      id: createParticipantLayoutScreenShareId({ ownerId, shareId }),
      isLocal: false,
      mediaStream: createParticipantLayoutMediaStream(consumer.track),
      ownerId,
      trackKey: getTrackKey(consumer.track)
    };
  });
}

function getDialogCameraMediaByOwnerId({ dialog, mySessionId } = {}) {
  const cameraMediaStreamsByOwnerId = {};
  const cameraTrackKeysByOwnerId = {};
  const localCameraTrack = getDialogLocalCameraTrack(dialog);

  if (localCameraTrack && mySessionId) {
    cameraMediaStreamsByOwnerId[mySessionId] = createParticipantLayoutMediaStream(localCameraTrack);
    cameraTrackKeysByOwnerId[mySessionId] = getTrackKey(localCameraTrack);
  }

  getDialogRemoteCameraConsumers(dialog).forEach(consumer => {
    const ownerId = consumer.appData.peerId;

    if (cameraMediaStreamsByOwnerId[ownerId]) {
      return;
    }

    cameraMediaStreamsByOwnerId[ownerId] = createParticipantLayoutMediaStream(consumer.track);
    cameraTrackKeysByOwnerId[ownerId] = getTrackKey(consumer.track);
  });

  return {
    cameraMediaStreamsByOwnerId,
    cameraTrackKeysByOwnerId
  };
}

export function getParticipantLayoutDialogMediaSnapshot({ dialog, mySessionId } = {}) {
  const localMicEnabled = typeof dialog?.isMicEnabled === "boolean" ? dialog.isMicEnabled : null;
  const { cameraMediaStreamsByOwnerId, cameraTrackKeysByOwnerId } = getDialogCameraMediaByOwnerId({
    dialog,
    mySessionId
  });
  const screenShares = [
    getDialogLocalShare(dialog, mySessionId),
    ...getDialogRemoteScreenShares(dialog, mySessionId)
  ].filter(Boolean);
  const primaryScreenShare = screenShares[0] || null;

  return {
    localMicEnabled,
    cameraMediaStreamsByOwnerId,
    cameraTrackKeysByOwnerId,
    screenShares,
    shareOwnerId: primaryScreenShare?.ownerId || null,
    shareMediaStream: primaryScreenShare?.mediaStream || null,
    shareTrackKey: primaryScreenShare?.trackKey || ""
  };
}
